import { Empty, Skeleton, Table } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getTenantCreditLedger } from '../../api'
import { formatDateTime as formatDateTimeI18n } from '../../../i18n/dateFormat'
import { useUiLocale } from '../../../i18n/LocaleProvider'
import type { CreditLedgerEntryRow, TenantOverview } from '../../types'

const PAGE_SIZE = 10

export default function CreditsTab({ overview, workspaceId }: { overview: TenantOverview; workspaceId: string }) {
  const { t } = useTranslation(['overview'])
  const { locale } = useUiLocale()
  const usage = overview.period_usage
  const [entries, setEntries] = useState<CreditLedgerEntryRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    getTenantCreditLedger(workspaceId, { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE })
      .then((r) => {
        setEntries(r.data.entries)
        setTotal(r.data.total)
      })
      .catch(() => {
        setEntries([])
        setTotal(0)
      })
      .finally(() => setLoading(false))
  }, [workspaceId, page])

  return (
    <div className="tc-ov-v3-credits" data-testid="overview-credits-tab">
      <div className="tc-ov-v3-metrics-row tc-ov-v3-metrics-row-2">
        <div className="tc-data-card tc-ov-v3-metric">
          <div className="tc-ov-v3-metric-head">{t('overview:tabs.v3.credits.balance')}</div>
          <div className="tc-ov-v3-metric-value">{usage?.credit_balance ?? overview.plan.credit_balance}</div>
        </div>
        <div className="tc-data-card tc-ov-v3-metric">
          <div className="tc-ov-v3-metric-head">{t('overview:tabs.v3.credits.usedMonth')}</div>
          <div className="tc-ov-v3-metric-value">{usage?.credits_used ?? '—'}</div>
          {usage?.my_credit_limit != null ? (
            <div className="tc-ov-v3-metric-sub">
              {t('overview:tabs.v3.credits.myLimit', {
                used: usage.my_credits_used ?? '—',
                limit: usage.my_credit_limit,
                remaining: usage.my_credits_remaining ?? '—',
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div className="tc-data-card tc-ov-v3-card">
        <div className="tc-ov-panel-title">{t('overview:tabs.v3.credits.ledgerTitle')}</div>
        {loading && entries.length === 0 ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : entries.length === 0 && !loading ? (
          <Empty description={t('overview:creditLedger.empty')} />
        ) : (
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            dataSource={entries}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              onChange: (p) => setPage(p),
              showSizeChanger: false,
            }}
            columns={[
              {
                title: t('overview:creditLedger.columns.time'),
                dataIndex: 'created_at',
                render: (v: string) => formatDateTimeI18n(v, locale),
              },
              {
                title: t('overview:creditLedger.columns.type'),
                dataIndex: 'transaction_type',
                render: (v: string) => t(`overview:creditLedger.transactionType.${v}`, { defaultValue: v }),
              },
              {
                title: t('overview:creditLedger.columns.amount'),
                dataIndex: 'amount',
                align: 'right' as const,
                render: (v: string, row: CreditLedgerEntryRow) => (
                  <span style={{ color: row.direction === 'credit' ? 'var(--success-text)' : 'var(--error-text)' }}>
                    {row.direction === 'credit' ? '+' : '−'}
                    {v}
                  </span>
                ),
              },
              {
                title: t('overview:creditLedger.columns.balanceAfter'),
                dataIndex: 'balance_after',
                align: 'right' as const,
              },
            ]}
          />
        )}
      </div>
    </div>
  )
}
